// src/components/admin/StudentList.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Table, Badge, Button,
  Form, InputGroup, Spinner, Pagination, Alert, Modal
} from 'react-bootstrap';
import {
  FaUserGraduate, FaCheckCircle, FaTimesCircle, FaHourglassHalf,
  FaSearch, FaEnvelope, FaCalendarAlt, FaUniversity, FaArrowLeft
} from 'react-icons/fa';

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [collegeSearch, setCollegeSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionNote, setActionNote] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states for approve/reject confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = !user.college_id;

  useEffect(() => {
    if (user?.college_id) {
      setSelectedCollege({ college_id: user.college_id, college_name: 'My College' });
    } else {
      fetchColleges();
    }
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/college');
      if (response.success && Array.isArray(response.message?.showCollege)) {
        setColleges(response.message.showCollege);
      }
    } catch (err) {
      setError('Failed to load colleges');
    } finally {
      setLoading(false);
    }
  };

  // Fetch students - IMPORTANT LOGIC CHANGE HERE
  const fetchStudents = async () => {
    if (!selectedCollege) return;
    try {
      setLoading(true);
      setError(null);

      let url;

      if (selectedCollege.college_id) {
        url = `/api/user-admin/users?collegeId=${selectedCollege.college_id}`;
      } 
      else {
        url = '/api/user-admin/users';
      }
      if (selectedStatus !== 'all') {
        url += `${url.includes('?') ? '&' : '?'}accountStatus=${selectedStatus}`;
      }

      const response = await apiService.get(url);

      if (response.success && Array.isArray(response.message?.users)) {
        let fetchedUsers = response.message.users;
        if (selectedCollege.college_id === null) {
          fetchedUsers = fetchedUsers.filter(user => user.college_id === null);
        }
        setStudents(fetchedUsers);
      } else {
        setError('Failed to load student list');
      }
    } catch (err) {
      setError('Error loading students: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCollege) {
      fetchStudents();
    }
  }, [selectedCollege, selectedStatus]);

  // Search filter (client-side)
  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredColleges = colleges.filter(college =>
    college.college_name?.toLowerCase().includes(collegeSearch.toLowerCase()) ||
    college.college_code?.toLowerCase().includes(collegeSearch.toLowerCase())
  );

  // Pagination calculation
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <Badge bg="success"><FaCheckCircle className="me-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge bg="danger"><FaTimesCircle className="me-1" /> Rejected</Badge>;
      case 'pending':
        return <Badge bg="warning"><FaHourglassHalf className="me-1" /> Pending</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  // Open confirmation modal
  const handleActionClick = (student, action) => {
    setSelectedStudent(student);
    setSelectedAction(action);
    setActionNote('');
    setShowConfirmModal(true);
  };

  // Confirm approve/reject
  const confirmAction = async () => {
    if (!selectedStudent || !selectedAction) return;

    const userId = selectedStudent.user_id;
    const status = selectedAction === 'approve' ? 'approved' : 'rejected';
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    try {
      setActionLoading(userId);
      setError(null);

      const payload = {
        account_status: status,
        mapping_notes: actionNote
      };

      if (selectedAction === 'approve') {
        payload.mapping_approved_at = new Date().toISOString();
        payload.mapping_approved_by = currentUser.user_id;
      } else {
        payload.mapping_rejected_at = new Date().toISOString();
        payload.mapping_rejected_by = currentUser.user_id;
      }

      const response = await apiService.patch(`/api/user-admin/users/${userId}/mapping`, payload);

      if (response.success) {
        setSuccessMsg(`Student ${status} successfully!`);
        fetchStudents(); // Refresh list
      } else {
        setError('Failed to update status');
      }
    } catch (err) {
      setError('Error updating status: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(null);
      setShowConfirmModal(false);
      setSelectedStudent(null);
      setSelectedAction(null);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">
            <FaUserGraduate className="me-2 text-primary" />
            Student Management
          </h2>
        </Col>
      </Row>

      {/* College Selection View */}
      {isSuperAdmin && !selectedCollege && (
        <>
          <InputGroup className="mb-4">
            <InputGroup.Text><FaSearch /></InputGroup.Text>
            <Form.Control
              placeholder="Search college by name or code..."
              value={collegeSearch}
              onChange={(e) => setCollegeSearch(e.target.value)}
            />
          </InputGroup>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Row>
              {/* Non-College Tile */}
              <Col md={4} lg={3} className="mb-4">
                <Card
                  className="shadow-sm h-100 text-center cursor-pointer"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedCollege({ college_id: null, college_name: 'All / System Users' })}
                >
                  <Card.Body className="d-flex flex-column justify-content-center">
                    <FaUserGraduate size={50} className="text-primary mb-3 mx-auto" />
                    <h5>All / System Users</h5>
                    <p className="text-muted small">View all users</p>
                  </Card.Body>
                </Card>
              </Col>

              {/* College Tiles */}
              {filteredColleges.map(college => (
                <Col md={4} lg={3} key={college.college_id} className="mb-4">
                  <Card
                    className="shadow-sm h-100 text-center cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCollege(college)}
                  >
                    <Card.Body className="d-flex flex-column justify-content-center">
                      <FaUniversity size={50} className="text-primary mb-3 mx-auto" />
                      <h5>{college.college_name}</h5>
                      {college.college_code && (
                        <Badge bg="info" className="mx-auto">{college.college_code}</Badge>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}

      {selectedCollege && (
        <>
          {isSuperAdmin && (
            <Button
              variant="outline-secondary"
              className="mb-4"
              onClick={() => {
                setSelectedCollege(null);
                setStudents([]);
                setCollegeSearch('');
                setSearchTerm('');
              }}
            >
              <FaArrowLeft className="me-2" /> Back to College List
            </Button>
          )}
          
          <h4 className="mb-4">
            Students of <strong>{selectedCollege.college_name}</strong>
          </h4>

      {/* Search + Filter */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={5}>
              <InputGroup>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, email or username..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>

            <Col md={4}>
              <Form.Select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </Col>

            <Col md={3} className="text-md-end">
              <Badge bg="info" className="fs-6 px-3 py-2">
                {filteredStudents.length} students
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Messages */}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Student Table / Loading / Empty State */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading students...</p>
        </div>
      ) : currentItems.length === 0 ? (
        <Card className="text-center py-5 border-0 shadow-sm">
          <Card.Body>
            <FaUserGraduate size={60} className="text-muted mb-3" />
            <h5 className="text-muted">No students found</h5>
            <p className="text-muted">
              {searchTerm || selectedStatus !== 'all' 
                ? 'Try different filters or search term' 
                : 'No students found in this view'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((student, index) => (
                    <tr key={student.user_id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: '40px', height: '40px', fontSize: '1.1rem' }}
                          >
                            {student.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <strong>{student.name}</strong>
                            <div className="text-muted small">{student.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{student.email}</td>
                      <td>{student.user_name}</td>
                      <td>
                        <Badge bg="secondary">
                          {student.role?.role_name || 'Student'}
                        </Badge>
                      </td>
                      <td>{getStatusBadge(student.account_status)}</td>
                      <td>
                        <small className="text-muted">
                          {new Date(student.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </small>
                      </td>
                      <td>
                        {student.account_status?.toLowerCase() === 'pending' && (
                          <div className="d-flex gap-2">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleActionClick(student, 'approve')}
                              disabled={actionLoading === student.user_id}
                            >
                              <FaCheckCircle className="me-1" />
                              Approve
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleActionClick(student, 'reject')}
                              disabled={actionLoading === student.user_id}
                            >
                              <FaTimesCircle className="me-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination size="sm">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2) {
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  }
                  return null;
                })}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </>
      )}
      </>
      )}

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedAction === 'approve' ? 'Approve Student' : 'Reject Student'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <>
              <p>Are you sure you want to <strong>{selectedAction}</strong> this student?</p>
              <div className="border p-3 rounded bg-light mt-3">
                <strong>{selectedStudent.name}</strong><br />
                <small className="text-muted">{selectedStudent.email} • {selectedStudent.user_name}</small>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Action Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  placeholder="Enter notes..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant={selectedAction === 'approve' ? 'success' : 'danger'}
            onClick={confirmAction}
            disabled={actionLoading === selectedStudent?.user_id}
          >
            {actionLoading === selectedStudent?.user_id ? (
              <Spinner animation="border" size="sm" />
            ) : (
              selectedAction === 'approve' ? 'Approve' : 'Reject'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StudentList;